import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import ProgressBar from '@/components/common/ProgressBar';
import { PROGRESS_BAR_COLORS } from '@/constants/customerLoyaltyProgram';
import { useAppSelector } from '@/redux';
import { getContent, getPercentProgress, getProgressBarProps } from '@/utils/customerLoyaltyUtils';
import styles from './UserSection.module.scss';

export const UserSection = ({ sectionData, currentLevel, nextLevel }) => {
	const router = useRouter();
	const { t } = useTranslation();

	const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
	const civility = useAppSelector(({ user }) => user.content?.civility);
	const civilityOther = useAppSelector(({ user }) => user.content?.civilityOther);
	const lastName = useAppSelector(({ user }) => user.content?.lastName);
	const levelAmountProgress = useAppSelector(({ user }) => user.content?.levelAmountProgress);
	const lastOrderDate = useAppSelector(({ user }) => user.content?.lastOrderDate);
	const locale = useAppSelector(({ root }) => root.storeConf.locale);
	const currencyISO = useAppSelector(({ root }) => root.storeConf.currencyISO);
	const dateFormat = useAppSelector(({ root }) => root.storeConf.dateFormat);

	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [btnLabel, setBtnLabel] = useState(null);
	const [btnUrl, setBtnUrl] = useState(null);

	useMemo(() => {
		const { title, description, btnLabel, btnUrl } = getContent(
			sectionData,
			loggedUser,
			civility,
			civilityOther,
			lastName,
			levelAmountProgress,
			lastOrderDate,
			currentLevel,
			nextLevel,
			t,
			locale,
			currencyISO,
			dateFormat,
		);

		setTitle(title);
		setDescription(description);
		setBtnLabel(btnLabel);
		setBtnUrl(btnUrl);
	}, [loggedUser, locale, currencyISO, currentLevel, nextLevel]);

	const handleBtnAction = () => {
		router.push(btnUrl);
	};

	return (
		<div className={styles.container}>
			<div className={styles['text-content']}>
				{title && <div dangerouslySetInnerHTML={{ __html: title }} className={styles.title} data-test-id="title" />}
				{description && (
					<div
						dangerouslySetInnerHTML={{ __html: description }}
						className={styles.description}
						data-test-id="description"
					/>
				)}
				{btnLabel && btnUrl && (
					<CallToActionContainer className={styles.btn} onClick={handleBtnAction}>
						{btnLabel}
					</CallToActionContainer>
				)}
			</div>

			<ProgressBar
				containerClassName={styles['progress-bar-container']}
				percentProgress={getPercentProgress(loggedUser, levelAmountProgress, currentLevel, nextLevel)}
				trackColor={PROGRESS_BAR_COLORS.UNCOMPLETED}
				progressIndicatorColor={PROGRESS_BAR_COLORS.COMPLETED}
				withCursor
				{...getProgressBarProps(sectionData, loggedUser, levelAmountProgress, currentLevel, nextLevel, locale, currencyISO)}
			/>
		</div>
	);
};

UserSection.propTypes = {
	sectionData: PropTypes.object.isRequired,
	currentLevel: PropTypes.object,
	nextLevel: PropTypes.object,
};

export default UserSection;
